import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  PhotoCamera,
  Image,
  Cancel,
  ArrowBack
} from '@mui/icons-material';
import merchantAPI from '../../../services/merchantAPI';
import { useNavigate, useLocation } from 'react-router-dom';
import FlutterServicesAPI from '../../../services/flutterServices';
import { AutoCloseAlert } from '../../Common';
import SecureImage from '../../Common/SecureImage';
import { API_URLS } from '../../../config/apiConfig';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import useRouteMeta from '../../../hooks/useRouteMeta';

const BrandsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useRouteMeta();
  const cameFromGeneration = Boolean(location.state?.returnToGenerate);
  const returnDraft = location.state?.aiGenerationDraft || null;
  const [brandLogos, setBrandLogos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [brandDialogError, setBrandDialogError] = useState('');

  const [brandLogoDialog, setBrandLogoDialog] = useState(false);
  const [editingBrandLogo, setEditingBrandLogo] = useState(null);
  const [brandLogoData, setBrandLogoData] = useState({ brand_name: '', description: '', brand_logo_path: '' });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedImageVariant, setSelectedImageVariant] = useState(0);
  const [variantProgress, setVariantProgress] = useState({});
  const [variantStatus, setVariantStatus] = useState({});
  const [backgroundRemovalOptions, setBackgroundRemovalOptions] = useState({ removeBackground: true, enhanceImage: true, sensitivity: 1.0 });

  const [deleteBrandLogoDialog, setDeleteBrandLogoDialog] = useState(false);
  const [brandLogoToDelete, setBrandLogoToDelete] = useState(null);
  const [deleteBrandLogoLoading, setDeleteBrandLogoLoading] = useState(false);

  const { hasPermission } = useMerchantPermissions();
  const canViewBrand = hasPermission('Brand Management', 'view');
  const canCreateBrand = hasPermission('Brand Management', 'create');
  const canEditBrand = hasPermission('Brand Management', 'update');
  const canDeleteBrand = hasPermission('Brand Management', 'delete');

  useEffect(() => { loadBrandLogos(); }, []);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('uploads/merchant_brand_logos/')) {
      const parts = imagePath.split('/');
      if (parts.length >= 4) {
        const merchant_id = parts[2];
        const filename = parts.slice(3).join('/');
        return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/merchant_brand_logos/${merchant_id}/${filename}`;
      }
    }
    if (imagePath.startsWith('uploads/brand_logos/')) {
      const filename = imagePath.replace('uploads/brand_logos/', '');
      return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/brand_logos/${filename}`;
    }
    if (imagePath.startsWith('uploads/')) {
      return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/${imagePath.replace('uploads/', '')}`;
    }
    return `${API_URLS.BACKEND_BASE}/api/merchant/auth/uploads/${imagePath}`;
  };

  const loadBrandLogos = async () => {
    try {
      const response = await merchantAPI.getBrandLogos();
      setBrandLogos(response.data.brand_logos || []);
    } catch (error) {
      console.error('Failed to load brand logos:', error);
    }
  };

  const handleAddBrandLogo = () => {
    setEditingBrandLogo(null);
    setBrandLogoData({ brand_name: '', description: '', brand_logo_path: '' });
    setBrandDialogError('');
    setBackgroundRemovalOptions({ removeBackground: true, enhanceImage: true, sensitivity: 1.0 });
    setSelectedFile(null);
    setShowPreview(false);
    setPreviewData(null);
    setSelectedImageVariant(0);
    setVariantProgress({});
    setVariantStatus({});
    setBrandLogoDialog(true);
  };

  const handleEditBrandLogo = (logo) => {
    setEditingBrandLogo(logo);
    setBrandLogoData({ brand_name: logo.brand_name, description: logo.description || '', brand_logo_path: logo.brand_logo_path || '' });
    setBrandDialogError('');
    setBackgroundRemovalOptions({ removeBackground: true, enhanceImage: true, sensitivity: 1.0 });
    setSelectedFile(null);
    setShowPreview(false);
    setPreviewData(null);
    setSelectedImageVariant(0);
    setVariantProgress({});
    setVariantStatus({});
    setBrandLogoDialog(true);
  };

  const handleDeleteBrandLogo = (logo) => {
    setBrandLogoToDelete(logo);
    setDeleteBrandLogoDialog(true);
  };

  const confirmDeleteBrandLogo = async () => {
    if (!brandLogoToDelete) return;
    setDeleteBrandLogoLoading(true);
    try {
      await merchantAPI.deleteBrandLogo(brandLogoToDelete.id);
      setSuccess('Brand logo deleted successfully');
      loadBrandLogos();
      setDeleteBrandLogoDialog(false);
      setBrandLogoToDelete(null);
    } catch (error) {
      console.error('Failed to delete brand logo:', error);
      setError('Failed to delete brand logo');
    } finally {
      setDeleteBrandLogoLoading(false);
    }
  };

  const handleSetPrimaryBrandLogo = async (logo) => {
    try {
      setLoading(true);
      await merchantAPI.setPrimaryBrandLogo(logo.id);
      setSuccess(`"${logo.brand_name}" set as your primary brand logo`);
      loadBrandLogos();
    } catch (error) {
      console.error('Failed to set primary brand logo:', error);
      setError(error.response?.data?.error || 'Failed to set primary brand logo');
    } finally {
      setLoading(false);
    }
  };

  const handleBrandLogoSubmit = async () => {
    setBrandDialogError('');
    if (!brandLogoData.brand_name.trim()) { setBrandDialogError('Brand name is required'); return; }
    if (!editingBrandLogo && !selectedFile) { setBrandDialogError('Please select a logo file'); return; }
    setUploadingLogo(true);
    try {
      let logoPath = brandLogoData.brand_logo_path;
      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('remove_background', backgroundRemovalOptions.removeBackground);
        formData.append('enhance_image', backgroundRemovalOptions.enhanceImage);
        formData.append('selected_variant', selectedImageVariant);
        const uploadResponse = await merchantAPI.uploadBrandLogo(formData);
        logoPath = uploadResponse.data.file_path;
      }
      const brandData = { ...brandLogoData, brand_logo_path: logoPath };
      if (editingBrandLogo) {
        await merchantAPI.updateBrandLogo(editingBrandLogo.id, brandData);
        setSuccess('Brand logo updated successfully');
      } else {
        await merchantAPI.createBrandLogo(brandData);
        setSuccess('Brand logo created successfully');
      }
      setBrandLogoDialog(false);
      setSelectedFile(null);
      setShowPreview(false);
      setPreviewData(null);
      setBackgroundRemovalOptions({ removeBackground: true, enhanceImage: true, sensitivity: 1.0 });
      loadBrandLogos();
    } catch (error) {
      console.error('Failed to save brand logo:', error);
      setBrandDialogError(error.response?.data?.error || 'Failed to save brand logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoUploadClick = async (event) => {
    if (event) event.stopPropagation();
    if (isCheckingPermissions) return;
    if (FlutterServicesAPI.isFlutterApp()) {
      try {
        setIsCheckingPermissions(true);
        const permissionResult = await FlutterServicesAPI.checkFilePermissions();
        if (!permissionResult.can_upload) { setError('File upload permission denied. Please check your device permissions.'); return; }
      } catch (error) {
        setError('Failed to check file permissions. Please try again.'); return;
      } finally {
        setIsCheckingPermissions(false);
      }
    }
    const fileInput = document.getElementById('logo-upload');
    if (fileInput) fileInput.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setBrandDialogError('');
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!allowedTypes.includes(file.type)) { setBrandDialogError('Please select only JPG, JPEG, PNG, WebP, or HEIC files.'); return; }
    const maxSize = 6 * 1024 * 1024;
    if (file.size > maxSize) { setBrandDialogError('File size must be less than 6MB.'); return; }
    setSelectedFile(file);
    setShowPreview(false);
    setPreviewData(null);
    await handlePreview(file);
  };

  const handlePreview = async (file = null) => {
    const fileToProcess = file || selectedFile;
    if (!fileToProcess) return;
    setPreviewLoading(true);
    setBrandDialogError('');
    setVariantProgress({});
    setVariantStatus({});
    try {
      const formData = new FormData();
      formData.append('file', fileToProcess);
      formData.append('remove_background', true);
      formData.append('enhance_image', true);

      const simulateVariantProgress = () => {
        const variants = ['original', 'variant1', 'variant2', 'variant3'];
        variants.forEach((variant, index) => {
          setVariantStatus(prev => ({ ...prev, [variant]: 'loading' }));
          setVariantProgress(prev => ({ ...prev, [variant]: 0 }));
          const progressInterval = setInterval(() => {
            setVariantProgress(prev => {
              const currentProgress = prev[variant] || 0;
              const newProgress = Math.min(currentProgress + Math.random() * 20, 100);
              if (newProgress >= 100) { clearInterval(progressInterval); setVariantStatus(prevStatus => ({ ...prevStatus, [variant]: 'completed' })); }
              return { ...prev, [variant]: newProgress };
            });
          }, 200 + (index * 100));
        });
      };
      simulateVariantProgress();
      const response = await merchantAPI.previewBrandLogo(formData);
      setPreviewData(response.data);
      setShowPreview(true);
      setTimeout(() => {
        const variants = ['original', 'variant1', 'variant2', 'variant3'];
        variants.forEach(variant => { setVariantStatus(prev => ({ ...prev, [variant]: 'completed' })); setVariantProgress(prev => ({ ...prev, [variant]: 100 })); });
      }, 1000);
    } catch (error) {
      console.error('Failed to preview logo:', error);
      setBrandDialogError(error.response?.data?.error || 'Failed to preview logo');
      const variants = ['original', 'variant1', 'variant2', 'variant3'];
      variants.forEach(variant => { setVariantStatus(prev => ({ ...prev, [variant]: 'error' })); });
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Container className="mt-mb-0" maxWidth="lg" sx={{ mt: { xs: 0, sm: 0, md: 4 }, mb: { xs: 0, sm: 2, md: 4 }, px: { xs: 0, sm: 2, md: 3 } }}>
      <Paper elevation={0} sx={{ p: { xs: 0.5, sm: 2, md: 4 }, pt: { xs: 0, sm: 3, md: 3 }, boxShadow: 'none', border: { xs: 'none', sm: '1px solid #e5e7eb', md: '1px solid #e5e7eb' }, bgcolor: { xs: 'transparent', sm: 'white', md: 'white' }, borderRadius: '8px' }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} px={{ xs: 0.5, sm: 0 }}>
          <Button onClick={() => {
            if (cameFromGeneration) {
              navigate('/generate', { state: { fromModelStudio: true, refreshBrandLogos: true, aiGenerationDraft: returnDraft } });
            } else {
              navigate('/dashboard');
            }
          }}
            startIcon={
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" viewBox="0 0 8 13" fill="none">
                <path d="M6.41309 0.916079L0.916291 6.41287L6.41309 11.9097" stroke="#651BC6" strokeWidth="1.83226" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            sx={{ color: '#4A5565', px: 0, textTransform: 'none', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '17.59px', '&:hover': { background: 'transparent', opacity: 0.8 } }}>
            Back
          </Button>
          <Typography variant="h6" sx={{ color: '#1E2939', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '17.59px' }}>Brands</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        <AutoCloseAlert severity="success" message={success} show={!!success} onClose={() => setSuccess('')} autoCloseDelay={4000} />

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5} mt={0.5}>
          <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '16px', color: '#101828' }}>My Brands ({brandLogos.length})</Typography>
          {canCreateBrand && (
            <Button variant="contained" startIcon={<img src="/assets/img/website/icons/add-br.svg" alt="Add" style={{ width: '13px', height: '13px' }} />} onClick={handleAddBrandLogo}
              sx={{ background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)', color: 'white', textTransform: 'none', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '14px', boxShadow: 'none', height: '40px', borderRadius: '10px', '&:hover': { background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)', opacity: 0.9, boxShadow: 'none' }, px: 3 }}>
              Add Brand
            </Button>
          )}
        </Box>

        {brandLogos.length === 0 ? (
          <Box textAlign="center" py={3}>
            <Image sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No Brands</Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>You haven't added any brands yet. Add your first brand to get started.</Typography>
            {canCreateBrand && (
              <Button variant="outlined" startIcon={<img src="/assets/img/website/icons/add-br.svg" alt="Add" style={{ width: '16px', height: '16px' }} />} onClick={handleAddBrandLogo}
                sx={{ borderColor: '#6318C6', color: '#6318C6', '&:hover': { borderColor: '#6318C6', backgroundColor: '#f3e8ff' } }}>
                Add Your First Brand
              </Button>
            )}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {brandLogos.map((logo) => (
              <Grid item xs={12} md={4} key={logo.id}>
                <Card elevation={0} sx={{ bgcolor: 'white', borderRadius: '15px', boxShadow: '0px 1.88px 11.27px 0px rgba(0, 0, 0, 0.08)' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', p: 2, position: 'relative', '&:last-child': { pb: 2 } }}>
                    {/* Image Box */}
                    {logo.brand_logo_path ? (
                      <Box sx={{ width: 60, height: 60, flexShrink: 0, borderRadius: '13.15px', overflow: 'hidden', border: '1.5px solid #A33236', boxShadow: '0px 1.88px 7.51px 0px rgba(0, 0, 0, 0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb', mr: 2 }}>
                        <SecureImage src={getImageUrl(logo.brand_logo_path)} alt={logo.brand_name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                      </Box>
                    ) : (
                      <Box sx={{ width: 60, height: 60, flexShrink: 0, borderRadius: '13.15px', border: '1.5px solid #A33236', boxShadow: '0px 1.88px 7.51px 0px rgba(0, 0, 0, 0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f9fafb', color: 'text.secondary', mr: 2 }}>
                        <Typography variant="caption" fontSize="10px" textAlign="center">No Logo</Typography>
                      </Box>
                    )}

                    {/* Info */}
                    <Box sx={{ flexGrow: 1, pr: 14 }}>
                      <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '15px', color: '#101828', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        {logo.brand_name}
                      </Typography>
                      <Typography sx={{ fontFamily: 'var(--font-montserrat-regular)', fontWeight: 400, fontSize: '14px', color: '#6A7282', mt: 0.5 }}>
                        {logo.description || 'No description available'}
                      </Typography>
                      <Typography sx={{ fontFamily: 'var(--font-montserrat-regular)', fontWeight: 400, fontSize: '11px', color: '#000000', mt: 0.5 }}>
                        Added on {new Date(logo.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </Typography>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, position: 'absolute', top: 16, right: 16, alignItems: 'center' }}>
                      <IconButton size="small" onClick={() => handleEditBrandLogo(logo)} sx={{ border: '1px solid #E5E7EB', borderRadius: '8px', p: 0.5, width: '32px', height: '32px' }}>
                        <img src="/assets/img/website/icons/edit-br.svg" alt="Edit" style={{ width: '16px', height: '16px' }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteBrandLogo(logo)} sx={{ border: '1px solid #FCA5A5', borderRadius: '8px', p: 0.5, width: '32px', height: '32px' }}>
                        <img src="/assets/img/website/icons/delete-br.svg" alt="Delete" style={{ width: '16px', height: '16px' }} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Brand Logo Dialog */}
      <Dialog open={brandLogoDialog} onClose={() => { setBrandLogoDialog(false); setBrandDialogError(''); setVariantProgress({}); setVariantStatus({}); }} maxWidth={false} fullWidth
        PaperProps={{
          sx: {
            borderRadius: '22px', m: 2,
            maxWidth: {
              xs: 'calc(100% - 30px)', // 📱 mobile
              sm: 'calc(100% - 60px)',
              md: '600px',             // 💻 desktop
              lg: '700px'
            },
            width: '100%', // important so maxWidth applies properly
            mx: 'auto'
          }
        }} >
        <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 }, background: 'linear-gradient(135deg, #651BC6 0%, #8B5CF6 100%)', position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 700, color: 'white', fontSize: '17px' }}>
              {editingBrandLogo ? 'Edit Brand' : 'Add New Brand'}
            </Typography>
            <IconButton onClick={() => { setBrandLogoDialog(false); setBrandDialogError(''); }} sx={{ color: 'white', p: 0 }}>
              <img src="/assets/img/website/icons/cl-icon.svg" alt="Close" style={{ width: '24px', height: '24px' }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 3 }, overflow: 'auto' }}>
          {brandDialogError && (<Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{brandDialogError}</Alert>)}

          <Box sx={{ mt: 1, mb: 2, py: 2 }}>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '13.3px', color: '#364153', mb: 1 }}>
              Brand Name <span style={{ color: '#6318C6' }}>*</span>
            </Typography>
            <TextField fullWidth placeholder="Enter brand name"
              value={brandLogoData.brand_name} onChange={(e) => setBrandLogoData({ ...brandLogoData, brand_name: e.target.value })}
              required error={!brandLogoData.brand_name.trim() && brandLogoData.brand_name !== ''}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '13.3px', height: '45px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '& .MuiInputBase-input': { fontFamily: 'var(--font-montserrat-regular)', fontSize: '13.3px', color: '#000000', '&::placeholder': { color: '#99A1AF', opacity: 1 } }
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '13.3px', color: '#364153', mb: 1 }}>
              Description <span style={{ color: '#6318C6' }}>*</span>
            </Typography>
            <TextField fullWidth placeholder="Brief description of the brand" multiline rows={3}
              value={brandLogoData.description} onChange={(e) => setBrandLogoData({ ...brandLogoData, description: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '13.3px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                '& .MuiInputBase-input': { fontFamily: 'var(--font-montserrat-regular)', fontSize: '13.3px', color: '#000000', '&::placeholder': { color: '#99A1AF', opacity: 1 } }
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '13.3px', color: '#364153', mb: 1 }}>
              Upload Logo (Optional)
            </Typography>
            <input accept=".jpg,.jpeg,.png,.webp,.heic,image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif" style={{ display: 'none' }} id="logo-upload" type="file" onChange={handleFileSelect} />
            <Button variant="outlined" onClick={handleLogoUploadClick} disabled={uploadingLogo}
              sx={{ bgcolor: '#ede9fe', border: 'none', color: '#6318C6', textTransform: 'none', fontFamily: 'var(--font-montserrat-semibold)', fontSize: '13.3px', px: 3, py: 1, borderRadius: '8px', '&:hover': { bgcolor: '#e2d5f8', border: 'none' } }}>
              Choose Files
            </Button>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#99A1AF', fontFamily: 'var(--font-montserrat-regular)', fontSize: '12px' }}>
              Upload your brand logo image (JPG, PNG)
            </Typography>
            {selectedFile && <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#000000', fontFamily: 'var(--font-montserrat-regular)', fontSize: '12px' }}>Selected: {selectedFile.name}</Typography>}
          </Box>

          {/* Step 3: Preview */}
          {selectedFile && (
            <Box sx={{ mb: 3 }}>
              {previewLoading && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <CircularProgress size={20} sx={{ color: '#6318C6' }} />
                    <Typography variant="body2" sx={{ color: '#6318C6', fontWeight: 500 }}>Processing your logo with AI...</Typography>
                  </Box>
                  <LinearProgress sx={{ height: 6, borderRadius: 3, backgroundColor: '#f3e8ff', '& .MuiLinearProgress-bar': { backgroundColor: '#6318C6', borderRadius: 3 } }} />
                </Box>
              )}

              {showPreview && previewData && (
                <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f8f9fa', borderRadius: 3, border: '1px solid #e5e7eb' }}>
                  <Grid container spacing={{ xs: 2, sm: 3 }}>
                    {previewData.processed && previewData.processed.variants && (
                      <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', mb: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>Choose Your Preferred Image</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Select from the original or AI-processed variants below</Typography>
                        </Box>
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                          {/* Original */}
                          <Grid item xs={6} sm={4} md={3}>
                            <Box sx={{ textAlign: 'center', cursor: 'pointer', p: { xs: 1, sm: 1.5 }, borderRadius: 3, border: selectedImageVariant === 0 ? '3px solid #6318C6' : '2px solid #e5e7eb', backgroundColor: selectedImageVariant === 0 ? '#f3f0ff' : 'white', transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden', height: { xs: 280, sm: 300 }, display: 'flex', flexDirection: 'column', '&:hover': { borderColor: '#6318C6', backgroundColor: '#f9f5ff' } }} onClick={() => setSelectedImageVariant(0)} role="button" tabIndex={0}>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>Original</Typography>
                              <Box sx={{ width: '100%', height: { xs: 100, sm: 120 }, borderRadius: 2, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', backgroundColor: '#f9fafb', position: 'relative' }}>
                                {variantStatus.original === 'loading' && (<Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.9)', zIndex: 2 }}><CircularProgress size={24} sx={{ color: '#6318C6', mb: 1 }} /><Typography variant="caption" sx={{ color: '#6318C6', fontSize: '0.7rem' }}>{Math.round(variantProgress.original || 0)}%</Typography></Box>)}
                                {variantStatus.original === 'completed' && previewData.original && (<img src={previewData.original.data} alt="Original logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />)}
                              </Box>
                              {previewData.original && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{(previewData.original.size / 1024).toFixed(1)} KB</Typography>}
                              {selectedImageVariant === 0 && (<Chip label="Selected" size="small" color="primary" sx={{ fontSize: '0.65rem', height: 18, mt: 1, fontWeight: 600 }} />)}
                            </Box>
                          </Grid>

                          {/* Processed Variants */}
                          {previewData.processed.variants.map((variant) => {
                            const variantKey = `variant${variant.index + 1}`;
                            const isVariantLoading = variantStatus[variantKey] === 'loading';
                            const isVariantCompleted = variantStatus[variantKey] === 'completed';
                            const isVariantError = variantStatus[variantKey] === 'error';
                            return (
                              <Grid item xs={6} sm={4} md={3} key={variant.index}>
                                <Box sx={{ textAlign: 'center', cursor: isVariantLoading ? 'not-allowed' : 'pointer', p: { xs: 1, sm: 1.5 }, borderRadius: 3, border: selectedImageVariant === (variant.index + 1) ? '3px solid #6318C6' : '2px solid #e5e7eb', backgroundColor: selectedImageVariant === (variant.index + 1) ? '#f3f0ff' : 'white', transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden', height: { xs: 280, sm: 300 }, display: 'flex', flexDirection: 'column', opacity: isVariantLoading ? 0.7 : 1, '&:hover': !isVariantLoading ? { borderColor: '#6318C6', backgroundColor: '#f9f5ff' } : {} }} onClick={() => !isVariantLoading && setSelectedImageVariant(variant.index + 1)} role="button" tabIndex={0}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#374151', fontSize: '0.85rem' }}>{variant.name}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.65rem', lineHeight: 1.3 }}>{variant.description}</Typography>
                                  <Box sx={{ width: '100%', height: { xs: 100, sm: 120 }, borderRadius: 2, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', backgroundColor: '#f9fafb', position: 'relative' }}>
                                    {isVariantLoading && (<Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.9)', zIndex: 2 }}><CircularProgress size={24} sx={{ color: '#6318C6', mb: 1 }} /><Typography variant="caption" sx={{ color: '#6318C6', fontSize: '0.7rem' }}>{Math.round(variantProgress[variantKey] || 0)}%</Typography></Box>)}
                                    {isVariantError && (<Typography variant="caption" sx={{ color: '#ef4444', fontSize: '0.7rem' }}>Error processing</Typography>)}
                                    {isVariantCompleted && (<img src={variant.data} alt={`${variant.name} logo variant`} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />)}
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.7rem' }}>{(variant.size / 1024).toFixed(1)} KB</Typography>
                                  <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                    {previewData.processed.background_removed && (<Chip label="BG Removed" size="small" color="success" sx={{ fontSize: '0.55rem', height: 16, fontWeight: 600 }} />)}
                                    {previewData.processed.image_enhanced && (<Chip label="Enhanced" size="small" color="primary" sx={{ fontSize: '0.55rem', height: 16, fontWeight: 600 }} />)}
                                  </Box>
                                  {selectedImageVariant === (variant.index + 1) && (<Chip label="Selected" size="small" color="primary" sx={{ fontSize: '0.65rem', height: 18, mt: 1, fontWeight: 600 }} />)}
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {/* Current Logo Display */}
          {brandLogoData.brand_logo_path && (
            <Box sx={{ p: 2, bgcolor: '#f0f9ff', borderRadius: 2, border: '1px solid #bae6fd' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#0369a1' }}>Current Logo</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ width: 60, height: 45, borderRadius: 1, overflow: 'hidden', border: '1px solid #0ea5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'white' }}>
                  <SecureImage src={getImageUrl(brandLogoData.brand_logo_path)} alt="Brand Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                </Box>
                <Typography variant="caption" color="text.secondary">This will be replaced with your new logo</Typography>
              </Box>
            </Box>
          )}

        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, justifyContent: 'center', gap: 0.5 }}>
          <Button variant="outlined" onClick={() => setBrandLogoDialog(false)}
            sx={{ borderColor: '#E5E7EB', color: '#364153', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '14px', px: 1.5, py: 1, borderRadius: '13.15px', textTransform: 'none', minHeight: '42px', minWidth: '125px' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleBrandLogoSubmit} disabled={loading || uploadingLogo || !brandLogoData.brand_name.trim()}
            sx={{ background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)', color: 'white', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '14px', px: 1.5, py: 1, borderRadius: '13.15px', textTransform: 'none', minHeight: '42px', minWidth: '125px', boxShadow: 'none', '&:hover': { background: 'linear-gradient(90deg, #A864ED 0%, #F5B760 100%)', opacity: 0.9, boxShadow: 'none' }, '&:disabled': { background: '#d1d5db', color: '#9ca3af' } }}>
            {uploadingLogo ? <CircularProgress size={20} sx={{ color: 'white' }} /> : (editingBrandLogo ? 'Update Brand' : 'Add Brand')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Brand Logo Confirmation Dialog */}
      <Dialog open={deleteBrandLogoDialog} onClose={() => setDeleteBrandLogoDialog(false)} maxWidth={false} fullWidth
        PaperProps={{
          sx: {
            borderRadius: '22px', m: 2,
            maxWidth: {
              xs: 'calc(100% - 30px)', // 📱 mobile
              sm: 'calc(100% - 60px)',
              md: '600px',             // 💻 desktop
              lg: '355px'              // making it slightly smaller because it's just a confirmation
            },
            width: '100%', // important so maxWidth applies properly
            mx: 'auto'
          }
        }} >
        <DialogContent sx={{ px: { xs: 2, sm: 4 }, py: { xs: 4, sm: 5 }, overflow: 'auto', textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: { xs: '17px', sm: '18px' }, color: '#101828', mb: 1.5 }}>
            Are you sure you want to delete this brand logo?
          </Typography>
          <Box sx={{ backgroundColor: '#fff4e5', py: 1.5, px: 2, borderRadius: '10px', display: 'inline-block' }}>
            <Typography sx={{ fontFamily: 'var(--font-montserrat-regular)', fontSize: '13px', color: '#663c00' }}>
              This action cannot be undone. The brand logo will be permanently removed from your account.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, justifyContent: 'center', gap: 1 }}>
          <Button variant="outlined" onClick={() => { setDeleteBrandLogoDialog(false); setBrandLogoToDelete(null); }}
            sx={{ borderColor: '#E5E7EB', color: '#364153', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '14px', px: 2, py: 1, borderRadius: '13.15px', textTransform: 'none', minHeight: '42px', minWidth: '120px' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDeleteBrandLogo} disabled={deleteBrandLogoLoading}
            sx={{ backgroundColor: '#ef4444', color: 'white', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '14px', px: 2, py: 1, borderRadius: '13.15px', textTransform: 'none', minHeight: '42px', minWidth: '120px', boxShadow: 'none', '&:hover': { backgroundColor: '#dc2626', boxShadow: 'none' }, '&:disabled': { backgroundColor: '#fca5a5', color: '#fef2f2' } }}>
            {deleteBrandLogoLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BrandsPage;
